// What a lens legend shows, and how sure it is. A scalar lens is one continuous colour ramp, so
// its legend is one gradient bar with a few tick labels (five swatches with blank captions read
// as orphans); a category lens is a list of swatches.

import type { LensResult } from '../world/api.ts';
import type { ClaimKind } from './parts.tsx';
import { uniqueKeys } from './keys.ts';

const PERCENT = 100;

/** Most tick labels under a bar: low, middle, high. */
const TICKS_MAX = 3;

interface LegendTick {
  key: string;
  label: string;
  /** Position along the bar, percent. */
  at: number;
  /** Which side of `at` the label hangs on, so the end labels stay inside the bar. */
  align: 'start' | 'mid' | 'end';
}

interface LegendRow {
  key: string;
  label: string;
  color: string;
}

type LegendModel =
  | { shape: 'ramp'; gradient: string; ticks: LegendTick[]; unit: string | null }
  | { shape: 'swatches'; rows: LegendRow[] };

/** Indices to label: every labelled stop, thinned to first, middle and last when there are more than TICKS_MAX. */
function tickIndices(labels: string[]): number[] {
  const labelled = labels.map((l, i) => (l === '' ? -1 : i)).filter((i) => i >= 0);
  if (labelled.length <= TICKS_MAX) {
    return labelled;
  }
  const mid = labelled[Math.floor((labelled.length - 1) / 2)];
  return [labelled[0], mid, labelled[labelled.length - 1]];
}

/**
 * Splits a trailing unit that every label repeats ("5.00 apart from USA", "2.50 apart from USA")
 * so it is written once under the bar; three long labels side by side would overprint.
 */
function splitUnit(labels: string[]): { labels: string[]; unit: string | null } {
  const whole = { labels, unit: null };
  if (labels.length < 2) {
    return whole;
  }

  const tails = labels.map((l) => l.slice(l.indexOf(' ') + 1));
  const unit = tails[0];
  const heads = labels.map((l) => l.slice(0, Math.max(0, l.indexOf(' '))));
  if (heads.some((h) => !/\d/.test(h)) || tails.some((t) => t !== unit)) {
    return whole;
  }

  return { labels: heads, unit };
}

export function legendModel(lens: LensResult): LegendModel {
  const stops = lens.legend;

  if (lens.kind === 'category') {
    const rows = stops.filter((s) => s.label !== '');
    const keys = uniqueKeys(rows.map((s) => `${s.label}|${s.color}`));
    return { shape: 'swatches', rows: rows.map((s, i) => ({ key: keys[i], label: s.label, color: s.color })) };
  }

  const span = Math.max(1, stops.length - 1);
  const at = (i: number) => (i / span) * PERCENT;
  const gradient = `linear-gradient(90deg, ${stops.map((s, i) => `${s.color} ${at(i)}%`).join(', ')})`;
  const picked = tickIndices(stops.map((s) => s.label));
  const { labels, unit } = splitUnit(picked.map((i) => stops[i].label));
  const keys = uniqueKeys(labels);
  const alignOf = (p: number): LegendTick['align'] => (p <= 0 ? 'start' : p >= PERCENT ? 'end' : 'mid');
  const ticks = picked.map((i, n) => ({ key: keys[n], label: labels[n], at: at(i), align: alignOf(at(i)) }));
  return { shape: 'ramp', gradient, ticks, unit };
}

// ─── Claim kind per lens (truth rule 6) ──────────────────────────────────────────────────

interface LensClaim {
  /** null = the lens states no claim of its own. */
  kind: ClaimKind | null;
  note?: string;
}

const DATA_LENSES = new Set(['economy', 'population', 'democracy', 'co2', 'unga', 'milex', 'hdi', 'resources', 'alliance']);

const OTHER_LENSES: Record<string, LensClaim> = {
  conflict: { kind: 'assessment' },
  political: { kind: 'assessment', note: 'Rule-derived from dated events' },
  freshness: { kind: null, note: 'Derived from the age of every dated fact' },
};

export function lensClaim(id: string): LensClaim {
  if (DATA_LENSES.has(id)) {
    return { kind: 'data' };
  }
  return OTHER_LENSES[id] ?? { kind: null };
}
